import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EquipmentAccordion = ({ equipmentData }) => {
  const [expandedSections, setExpandedSections] = useState(new Set(['safety']));
  const [searchTerm, setSearchTerm] = useState('');

  if (!equipmentData) return null;

  const equipmentSections = [
    {
      id: 'safety',
      title: 'Seguridad',
      icon: 'Shield',
      items: equipmentData?.safety || [],
      color: 'text-red-600'
    },
    {
      id: 'engine',
      title: 'Motor y Rendimiento',
      icon: 'Zap',
      items: equipmentData?.engine || [],
      color: 'text-blue-600'
    },
    {
      id: 'drivetrain',
      title: 'Transmisión y Tracción',
      icon: 'Settings',
      items: equipmentData?.drivetrain || [],
      color: 'text-green-600'
    },
    {
      id: 'suspension',
      title: 'Suspensión y Dirección',
      icon: 'Activity',
      items: equipmentData?.suspension || [],
      color: 'text-cyan-600'
    },
    {
      id: 'wheels',
      title: 'Rines y Neumáticos',
      icon: 'Circle',
      items: equipmentData?.wheels || [],
      color: 'text-gray-600'
    },
    {
      id: 'comfort',
      title: 'Confort e Interior',
      icon: 'Armchair',
      items: equipmentData?.comfort || [],
      color: 'text-indigo-600'
    },
    {
      id: 'infotainment',
      title: 'Entretenimiento',
      icon: 'Smartphone',
      items: equipmentData?.infotainment || [],
      color: 'text-purple-600'
    }
  ].filter(section => section.items.length > 0);

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded?.has(sectionId)) {
      newExpanded?.delete(sectionId);
    } else {
      newExpanded?.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const expandAll = () => {
    setExpandedSections(new Set(equipmentSections.map(section => section.id)));
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  const filteredSections = equipmentSections?.map(section => ({
    ...section,
    items: section?.items?.filter(item =>
      item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    )
  }));

  const totalItems = equipmentSections?.reduce((sum, section) => sum + section?.items?.length, 0);
  const availableItems = equipmentSections?.reduce((sum, section) => 
    sum + section?.items?.filter(item => item?.available)?.length, 0
  );

  return (
    <div className="bg-card border border-border rounded-lg p-4 shadow-elevation">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
            <Icon name="List" size={16} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Equipamiento</h3>
            <p className="text-sm text-muted-foreground">
              {availableItems} de {totalItems} características disponibles
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={expandAll}
            iconName="Expand"
            iconSize={12}
          >
            Expandir Todo
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={collapseAll}
            iconName="Minimize"
            iconSize={12}
          >
            Contraer Todo
          </Button>
        </div>
      </div>
      {/* Search - Compact */}
      <div className="mb-4">
        <div className="relative">
          <Icon name="Search" size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar equipamiento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
        </div>
      </div>
      {/* Equipment Sections - Compact spacing */}
      <div className="space-y-3">
        {filteredSections?.map((section) => {
          const isExpanded = expandedSections?.has(section?.id);
          const hasItems = section?.items?.length > 0;
          
          return (
            <div key={section?.id} className="border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full flex items-center justify-between p-3 bg-muted hover:bg-muted/80 transition-smooth"
              >
                <div className="flex items-center space-x-3">
                  <Icon name={section?.icon} size={18} className={section?.color} />
                  <div className="text-left">
                    <h4 className="font-medium text-foreground text-sm">{section?.title}</h4>
                    <p className="text-xs text-muted-foreground">
                      {section?.items?.filter(item => item?.available)?.length} características
                    </p>
                  </div>
                </div>
                <Icon 
                  name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                  size={16} 
                  className="text-muted-foreground" 
                />
              </button>
              {isExpanded && hasItems && (
                <div className="p-3 bg-background">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {section?.items?.map((item, index) => (
                      <div
                        key={index}
                        className={`flex items-start space-x-2 p-2 rounded-lg ${
                          item?.available 
                            ? 'bg-success/5 border border-success/20' :'bg-muted border border-border'
                        }`}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {item?.available ? (
                            <Icon name="CheckCircle" size={14} className="text-success" />
                          ) : (
                            <Icon name="XCircle" size={14} className="text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className={`text-xs font-medium ${
                            item?.available ? 'text-foreground' : 'text-muted-foreground'
                          }`}>
                            {item?.name}
                          </h5>
                          <p className={`text-xs mt-0.5 ${
                            item?.available ? 'text-muted-foreground' : 'text-muted-foreground/70'
                          }`}>
                            {item?.description}
                          </p>
                          {item?.code && (
                            <p className="text-xs font-mono text-muted-foreground mt-0.5">
                              Código: {item?.code}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {isExpanded && !hasItems && (
                <div className="p-3 bg-background text-center">
                  <Icon name="Search" size={20} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No se encontraron características que coincidan con la búsqueda
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Summary - Compact */}
      <div className="mt-4 pt-3 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center">
            <div className="text-xl font-bold text-success">{availableItems}</div>
            <div className="text-xs text-muted-foreground">Disponibles</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-muted-foreground">{totalItems - availableItems}</div>
            <div className="text-xs text-muted-foreground">No Disponibles</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-primary">{equipmentSections?.length}</div>
            <div className="text-xs text-muted-foreground">Categorías</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-foreground">
              {Math.round((availableItems / totalItems) * 100)}%
            </div>
            <div className="text-xs text-muted-foreground">Completitud</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentAccordion;